import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { QrCode, LogOut, Settings as SettingsIcon, CreditCard, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import AlertsBell from "@/components/AlertsBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar({ showAuthLinks = true }) {
  const { user, logout } = useAuth();
  const { isPro } = useBilling();
  const navigate = useNavigate();

  return (
    <header
      data-testid="app-navbar"
      className="sticky top-0 z-40 w-full border-b border-slate-200/80 bg-white/85 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
            <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-xl tracking-tight text-slate-900">
            Qdurator
          </span>
        </Link>

        <nav className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:inline">
                <Button variant="ghost" size="sm" data-testid="nav-dashboard-link">
                  Dashboard
                </Button>
              </Link>
              <Link to="/team" className="hidden sm:inline">
                <Button variant="ghost" size="sm" data-testid="nav-team-link">
                  Team
                </Button>
              </Link>
              {!isPro && (
                <Link to="/pricing" className="hidden sm:inline">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold"
                    data-testid="nav-upgrade-link"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    Upgrade
                  </Button>
                </Link>
              )}
              <AlertsBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white flex items-center justify-center text-xs font-bold">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-slate-700 hidden sm:inline max-w-[120px] truncate">
                      {user.name || user.email}
                    </span>
                    {isPro && (
                      <span className="hidden sm:inline px-1.5 py-0.5 rounded bg-blue-600 text-white text-[9px] font-black tracking-wider">
                        PRO
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-xs text-slate-500">Signed in as</div>
                    <div className="font-semibold text-slate-900 truncate">
                      {user.email}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer" data-testid="nav-settings-link">
                      <SettingsIcon className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/pricing" className="cursor-pointer" data-testid="nav-billing-link">
                      <CreditCard className="w-4 h-4 mr-2" />
                      {isPro ? "Manage plan" : "Upgrade to Pro"}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="nav-logout-btn"
                    onClick={() => {
                      logout();
                      navigate("/");
                    }}
                    className="cursor-pointer text-red-600 focus:text-red-700"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : showAuthLinks ? (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login-btn">
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="nav-register-btn"
                >
                  Get started
                </Button>
              </Link>
            </>
          ) : null}
        </nav>
      </div>
    </header>
  );
}
